/**
 * Room Key Generator Utility
 *
 * Generates stable, unique keys for room selections that can be used to match rooms
 * across different API calls. Based on the getRoomKey function from CheckoutHotelRoomSelection.
 */

/**
 * Generate a key string from room tags
 */
const tagsKey = tags => {
  return tags
    ?.map(t => t.label?.toLowerCase()?.replace(/ /g, ''))
    ?.sort()
    ?.join('#');
};

/**
 * Generate a key string from promotions
 */
const getPromotionsKey = (promos = []) => {
  const sorted = promos
    .map(promotion => promotion.toLowerCase().replace(/\s+/g, ''))
    .sort();
  return JSON.stringify(sorted);
};

/**
 * Generate a unique key for a room based on its properties
 *
 * @param {Object} room - The room object
 * @param {number} depth - Depth level for key generation (default: 0)
 * @returns {string} Unique room identifier
 */
export const generateRoomKey = (room, depth = 0, bedId = null) => {
  const firstBed = room?.bedChoices?.[0];

  if (!firstBed) return null;

  const selectedBed = room.bedChoices.find(b => b.id === bedId) ?? firstBed;
  const supplierCode = room.supplierCode || room.supplier;
  const tags = tagsKey(selectedBed.tags);
  const promotions = getPromotionsKey(selectedBed.promotions);

  /**
   *We use a specific delimiter [bed:...] to encapsulate the bed ID because
   *its format is not uniform across suppliers
   */

  return `${room.name}_${supplierCode}_${room.source}_${selectedBed.totalPriceWithPromo}_${depth}_${tags}_${promotions}[bed:${selectedBed.id}]`;
};

/**
 * Find a room in available rooms by its key
 *
 * @param {string} roomKey - The room key to search for
 * @param {Array} availableRooms - Array of available room objects
 * @returns {Object|null} The matching room object or null if not found
 */
export const findRoomByKey = (roomKey, availableRooms) => {
  if (!roomKey || !availableRooms || availableRooms.length === 0) {
    return null;
  }

  // Search through available rooms to find match by key
  // Try different depth levels as rooms may have different depths
  for (let depth = 0; depth < 10; depth++) {
    for (const room of availableRooms) {
      if (generateRoomKey(room, depth) === roomKey) {
        return room;
      }
    }
  }

  return null;
};

/**
 * Find multiple rooms by comma-separated room keys
 *
 * @param {string} roomKeys - Comma-separated room keys string
 * @param {Array} availableRooms - Array of available room objects
 * @returns {Array} Array of matching room objects
 */
export const findRoomsByKeys = (roomKeys, availableRooms) => {
  if (!roomKeys || !availableRooms || availableRooms.length === 0) {
    return [];
  }

  // Split comma-separated keys and find each room
  const keys = roomKeys
    .split(',')
    .map(k => k.trim())
    .filter(Boolean);
  const matchedRooms = keys
    .map(key => findRoomByKey(key, availableRooms))
    .filter(Boolean);

  return matchedRooms;
};

/**
 * Check if two rooms are the same based on their key
 *
 * @param {Object} room1 - First room object
 * @param {Object} room2 - Second room object
 * @param {number} depth - Depth level for comparison (default: 0)
 * @returns {boolean} True if rooms match
 */
export const areRoomsSame = (room1, room2, depth = 0) => {
  if (!room1 || !room2) return false;
  return generateRoomKey(room1, depth) === generateRoomKey(room2, depth);
};

/**
 * Parse room key to extract parameters like bed ID and depth
 *
 * @param {string} key - The room key string
 * @returns {Object} An object containing extracted parameters (bedId, depth)
 */

export const parseKeyParams = key => {
  const bedId = key.match(/\[bed:(.+?)\]$/)?.[1] ?? null;
  const depth = parseInt(key.split('_')[4], 10);

  return { bedId, depth };
};
